import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaPhoneAlt, FaEnvelope, FaClock, FaMapMarkerAlt } from 'react-icons/fa';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  // Form Validation Logic
  const validate = () => {
    let tempErrors = {};
    if (!formData.name.trim()) tempErrors.name = "Name is required";
    if (!formData.email.match(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/)) tempErrors.email = "Invalid email format";
    if (!formData.subject.trim()) tempErrors.subject = "Subject is required";
    if (formData.message.length < 10) tempErrors.message = "Message must be at least 10 characters";
    
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      setIsSubmitting(true);
      // Simulating API call
      setTimeout(() => {
        setIsSubmitting(false);
        setSuccessMsg("Thank you! Your message has been sent successfully.");
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setSuccessMsg(''), 5000);
      }, 2000);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear error as user types
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* 1. HERO SECTION */}
      <section 
        className="relative h-[40vh] flex items-center justify-center bg-cover bg-center bg-blurry-sm"
        style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url('/Adam & Eve Day(4).jpeg')` }}
      >
        <div className="text-center text-white">
          <motion.h1 
            initial={{ y: 20, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            className="text-5xl md:text-6xl font-serif font-black uppercase tracking-tight"
          >
            Contact Us
          </motion.h1>
          <div className="h-1.5 w-20 bg-[#f9b50b] mx-auto mt-4"></div>
          <p className="mt-4 uppercase tracking-[0.3em] text-xs font-bold">
            <Link to="/" className="hover:text-[#f9b50b] text-gray-300 transition-colors">Home</Link>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-white">Contact</span>
          </p>
        </div>
      </section>

      {/* 2. GET IN TOUCH HEADER */}
      <section className="pt-20 pb-10 text-center">
        <h2 className="text-5xl font-serif font-bold uppercase text-[#221f1f] mb-2">Get In Touch</h2>
        <p className="text-gray-400 text-sm uppercase tracking-widest font-bold">Contact Us</p>
        <div className="h-[2px] w-16 bg-[#f9b50b] mx-auto mt-6"></div>
      </section>

      {/* 3. MAIN CONTACT CONTENT */}
      <section className="pb-24 px-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          
          {/* LEFT COLUMN: CONTACT INFO */}
          <div className="lg:col-span-1 space-y-12">
            <div className="flex gap-5">
              <FaMapMarkerAlt className="text-[#f9b50b] text-2xl mt-1" />
              <div >
                <h4 className="font-serif font-bold text-xl uppercase mb-3">Office Address</h4>
                <a 
                  href="https://www.google.com/maps/search/?api=1&query=1645+W+Irving+Park+Rd+Suite+100+Hanover+Park+IL+60133" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-gray-500 text-sm not-italic leading-relaxed hover:text-[#f9b50b] transition-colors"
                >
                  1645 W Irving Park Rd, Suite # 100 <br />
                  Hanover Park, IL, United States, Illinois 60133
                </a>
              </div>
            </div>

            <div className="flex gap-5">
              <FaPhoneAlt className="text-[#f9b50b] text-2xl mt-1" />
              <div>
                <h4 className="font-serif font-bold text-xl uppercase mb-3">Phone</h4>
                <a href="tel:18477377065" className="text-gray-500 text-sm hover:text-[#f9b50b] transition-colors">+1 (847) 737-7065</a>
              </div>
            </div>

            <div className="flex gap-5">
              <FaEnvelope className="text-[#f9b50b] text-2xl mt-1" />
              <div>
                <h4 className="font-serif font-bold text-xl uppercase mb-3">Email</h4>
                <a href="mailto:info@ilinterfaith.org" className="text-gray-500 text-sm hover:text-[#f9b50b] transition-colors">info@ilinterfaith.org</a>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: CONTACT FORM */}
          <div className="lg:col-span-2">
            <h3 className="text-2xl font-serif font-bold uppercase mb-8">Send Us Email</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <input 
                    type="text" name="name" value={formData.name} onChange={handleChange}
                    placeholder="Enter your full name" 
                    className={`w-full p-4 bg-gray-50 border ${errors.name ? 'border-red-500' : 'border-gray-100'} focus:outline-none focus:bg-white focus:border-[#f9b50b] transition-all text-sm`}
                  />
                  {errors.name && <p className="text-red-500 text-[10px] uppercase font-bold pl-1">{errors.name}</p>}
                </div>
                <div className="space-y-1">
                  <input 
                    type="email" name="email" value={formData.email} onChange={handleChange}
                    placeholder="Enter your email" 
                    className={`w-full p-4 bg-gray-50 border ${errors.email ? 'border-red-500' : 'border-gray-100'} focus:outline-none focus:bg-white focus:border-[#f9b50b] transition-all text-sm`}
                  />
                  {errors.email && <p className="text-red-500 text-[10px] uppercase font-bold pl-1">{errors.email}</p>}
                </div>
              </div>
              
              <div className="space-y-1">
                <input 
                  type="text" name="subject" value={formData.subject} onChange={handleChange}
                  placeholder="Subject" 
                  className={`w-full p-4 bg-gray-50 border ${errors.subject ? 'border-red-500' : 'border-gray-100'} focus:outline-none focus:bg-white focus:border-[#f9b50b] transition-all text-sm`}
                />
                {errors.subject && <p className="text-red-500 text-[10px] uppercase font-bold pl-1">{errors.subject}</p>}
              </div>

              <div className="space-y-1">
                <textarea 
                  name="message" rows="8" value={formData.message} onChange={handleChange}
                  placeholder="Write your message" 
                  className={`w-full p-4 bg-gray-50 border ${errors.message ? 'border-red-500' : 'border-gray-100'} focus:outline-none focus:bg-white focus:border-[#f9b50b] transition-all text-sm`}
                ></textarea>
                {errors.message && <p className="text-red-500 text-[10px] uppercase font-bold pl-1">{errors.message}</p>}
              </div>

              <button 
                disabled={isSubmitting}
                className="bg-[#221f1f] text-white font-bold uppercase tracking-widest px-10 py-4 hover:bg-[#221f1f] transition-all disabled:bg-gray-400 mt-4 text-xs shadow-lg"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>

              {successMsg && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  className="mt-6 p-4 bg-green-50 border border-green-200"
                >
                  <p className="text-green-700 font-bold text-center text-sm uppercase tracking-tight">
                    {successMsg}
                  </p>
                </motion.div>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;